import { Component, OnInit } from '@angular/core';
import { forkJoin } from 'rxjs';
import { AjaxService } from 'src/app/service/ajax.service';
import { UtilService } from 'src/app/service/util.service';
import { API } from 'src/environments/environment';

interface FluxoRealizadoLinha {
  dataIso: string;
  dataExibicao: string;
  diaSemana: string;
  saldoInicial: number;
  entradas: number;
  saidas: number;
  saldoFinal: number;
}

@Component({
  selector: 'app-fluxo-caixa-diario-realizado',
  templateUrl: './fluxo-caixa-diario-realizado.component.html',
  styleUrls: ['./fluxo-caixa-diario-realizado.component.scss']
})
export class FluxoCaixaDiarioRealizadoComponent implements OnInit {
  ano = new Date().getFullYear();
  saldoInicialPeriodo = 0;
  linhas: FluxoRealizadoLinha[] = [];

  saldoDiaData = '';
  saldoDiaValor = 0;
  saldoFinalData = '';
  saldoFinalValor = 0;

  loading = false;
  arrastandoTabela = false;
  private posicaoInicialX = 0;
  private scrollInicialX = 0;

  constructor(
    private ajaxService: AjaxService,
    private util: UtilService
  ) {}

  ngOnInit(): void {
    this.carregarRelatorio();
  }

  alterarAno(delta: number): void {
    this.ano += delta;
    this.carregarRelatorio();
  }

  carregarRelatorio(): void {
    const inicioAno = `${this.ano}-01-01`;
    const fimAno = `${this.ano}-12-31`;
    const urlReceber = `${API}financeiro/FinContaReceber/ConsultarFinContaReceber?dataRecebimentoInicio=${inicioAno}&dataRecebimentoFim=${fimAno}&pago=true`;
    const urlPagar = `${API}financeiro/FinContaPagar/ConsultarFinContaPagar`;

    this.loading = true;
    this.util.showLoading();

    forkJoin({
      receber: this.ajaxService.get(urlReceber),
      pagar: this.ajaxService.get(urlPagar)
    }).subscribe({
      next: ({ receber, pagar }) => {
        this.loading = false;
        this.util.closeLoading();

        const receberLista = this.extrairListaResponse(receber)
          .map((x: any) => this.util.converterParaLowercase(x || {}))
          .filter((x: any) => Boolean(x.pago))
          .filter((x: any) => this.ehPrevisaoRealR(x.previsaoreal));

        const pagarLista = this.extrairListaResponse(pagar)
          .map((x: any) => this.util.converterParaLowercase(x || {}))
          .filter((x: any) => Boolean(x.pago))
          .filter((x: any) => this.ehPrevisaoRealR(x.previsaoreal));

        this.montarLinhas(receberLista, pagarLista);
      },
      error: () => {
        this.loading = false;
        this.util.closeLoading();
        this.linhas = [];
        this.util.toasterShowError('Erro', 'Nao foi possivel carregar o fluxo diario realizado.');
      }
    });
  }

  formatMoney(valor: number): string {
    return Number(valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  /** Exibe traco quando valor zero (como no relatorio legado). */
  formatEntradaSaida(valor: number): string {
    const v = Number(valor || 0);
    if (v === 0) return '-';
    return `R$ ${this.formatMoney(v)}`;
  }

  iniciarArrasteTabela(event: MouseEvent, container: HTMLElement): void {
    if (event.button !== 0) return;
    const alvo = event.target as HTMLElement;
    if (alvo?.closest('button, a, input, select, textarea, label')) return;
    this.arrastandoTabela = true;
    this.posicaoInicialX = event.clientX;
    this.scrollInicialX = container.scrollLeft;
  }

  moverArrasteTabela(event: MouseEvent, container: HTMLElement): void {
    if (!this.arrastandoTabela) return;
    const deslocamento = event.clientX - this.posicaoInicialX;
    container.scrollLeft = this.scrollInicialX - deslocamento;
    event.preventDefault();
  }

  encerrarArrasteTabela(): void {
    this.arrastandoTabela = false;
  }

  private montarLinhas(receberLista: any[], pagarLista: any[]): void {
    const mapaEntradas = new Map<string, number>();
    const mapaSaidas = new Map<string, number>();

    receberLista.forEach((item: any) => {
      const data = this.normalizarDataIso(item.datarecebimento);
      if (!data || !data.startsWith(`${this.ano}-`)) return;
      const valRec =
        item.valorrecebido != null && item.valorrecebido !== '' ? Number(item.valorrecebido) : NaN;
      const valor = !Number.isNaN(valRec) ? valRec : Number(item.valordocumento || 0);
      if (Number.isNaN(valor) || valor === 0) return;
      mapaEntradas.set(data, Number(mapaEntradas.get(data) || 0) + valor);
    });

    pagarLista.forEach((item: any) => {
      const dataRef = item.datapagamento || item.databaixa;
      const data = this.normalizarDataIso(dataRef);
      if (!data || !data.startsWith(`${this.ano}-`)) return;
      const valPago = item.valorpago != null && item.valorpago !== '' ? Number(item.valorpago) : NaN;
      const valor = !Number.isNaN(valPago) ? valPago : Number(item.valordocumento || 0);
      if (Number.isNaN(valor) || valor === 0) return;
      mapaSaidas.set(data, Number(mapaSaidas.get(data) || 0) + valor);
    });

    const linhas: FluxoRealizadoLinha[] = [];
    let saldoCorrente = Number(this.saldoInicialPeriodo || 0);
    const data = new Date(this.ano, 0, 1);
    const fim = new Date(this.ano, 11, 31);

    while (data <= fim) {
      const iso = this.dateToIso(data);
      const entradas = Number(mapaEntradas.get(iso) || 0);
      const saidas = Number(mapaSaidas.get(iso) || 0);
      const saldoInicial = saldoCorrente;
      const saldoFinal = saldoInicial + entradas - saidas;

      linhas.push({
        dataIso: iso,
        dataExibicao: this.dateToBr(data),
        diaSemana: this.diaSemanaPt(data),
        saldoInicial,
        entradas,
        saidas,
        saldoFinal
      });

      saldoCorrente = saldoFinal;
      data.setDate(data.getDate() + 1);
    }

    this.linhas = linhas;
    const hojeIso = this.dateToIso(new Date());
    const linhaSaldoDia = linhas.find((x) => x.dataIso === hojeIso) || linhas[0];
    this.saldoDiaData = linhaSaldoDia?.dataExibicao || '';
    this.saldoDiaValor = Number(linhaSaldoDia?.saldoFinal || 0);
    this.saldoFinalData = linhas.length ? linhas[linhas.length - 1].dataExibicao : '';
    this.saldoFinalValor = linhas.length ? Number(linhas[linhas.length - 1].saldoFinal || 0) : 0;
  }

  private ehPrevisaoRealR(valor: any): boolean {
    return String(valor || 'R').trim().toUpperCase() === 'R';
  }

  private normalizarDataIso(valor: any): string {
    if (!valor) return '';
    const data = new Date(valor);
    if (Number.isNaN(data.getTime())) return '';
    return this.dateToIso(data);
  }

  private dateToIso(data: Date): string {
    const ano = data.getFullYear();
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const dia = String(data.getDate()).padStart(2, '0');
    return `${ano}-${mes}-${dia}`;
  }

  private dateToBr(data: Date): string {
    return data.toLocaleDateString('pt-BR');
  }

  private diaSemanaPt(data: Date): string {
    return data.toLocaleDateString('pt-BR', { weekday: 'long' });
  }

  private extrairListaResponse(response: any): any[] {
    let atual = response;
    for (let i = 0; i < 3; i++) {
      if (Array.isArray(atual)) break;
      if (atual && typeof atual === 'object' && Object.prototype.hasOwnProperty.call(atual, 'data') && atual.data != null) {
        atual = atual.data;
      } else {
        break;
      }
    }
    if (Array.isArray(atual)) return atual;
    if (atual && typeof atual === 'object') return Object.values(atual).filter((x: any) => x && typeof x === 'object');
    return [];
  }
}
